import { ChangeEvent, Dispatch, useEffect, useReducer } from 'react';
import { SelectChangeEvent } from '@mui/material/Select';
import dayjs from 'dayjs';

import { CustomInput, CustomSelect, MultipleSelect, Text } from '../../../shared/uiComponents';
import { validateName } from '../../../shared/Helpers/functions';
import {
	ACTIONS,
	ActionProperties,
	AssessmentErrorReducer,
	ERRORS,
	initialAssessmentErrorState,
} from './assessmentReducer';
import { useAssessmentOptions, useIsTelehealth } from '../hook';
import { Behaviors, SignatureInput, Targets, TelehealthLocations, TimeInfo } from '../components';
import { useSoapNoteFieldDisabled } from '../helpers';

import { useSelector } from '../../../redux/store';
import { UpdateAssessmentProperties } from '../../../redux/API/ClientAPIHelpers/soapNoteProperties';

const AssessmentData = ({
	data,
	setData,
	save,
}: {
	data: UpdateAssessmentProperties;
	save: () => void;
	setData: Dispatch<ActionProperties>;
}) => {
	const today = new Date();
	const todayString = dayjs(today).format('MM/DD/YYYY');

	const [errors, setErrors] = useReducer(AssessmentErrorReducer, initialAssessmentErrorState);

	const locationList = useSelector((state) => state.soapNote.locations);
	const errorList = useSelector((state) => state.soapNote.error?.errors);

	const { getFieldDisabled } = useSoapNoteFieldDisabled();
	const isTelehealth = useIsTelehealth(data.locationId);
	const { assessmentToolsList, peopleAttendanceList, treatmentPlanList, assessmentActivityList } =
		useAssessmentOptions();

	useEffect(() => {
		if (data.participantResponses.length) return;
		const payload = peopleAttendanceList.filter((x) => x.isDefault).map((y) => y.id);
		setData({ type: ACTIONS.setParticipants, payload });
	}, [peopleAttendanceList, setData, data]);

	useEffect(() => {
		if (!errorList) return;
		const list = Object.keys(errorList);
		list.forEach((id) => setErrors({ type: ERRORS[id], payload: true }));
	}, [errorList]);

	const selectAssessmentToolHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ payload: value, type: ACTIONS.setAssessmentTools });
		if (!errors.AssessmentToolResponses) return;
		setErrors({ payload: false, type: ERRORS.AssessmentToolResponses });
	};

	const selectAssessmentActivityHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ payload: value, type: ACTIONS.setAssessmentActivities });
		if (!errors.AssessmentActivityResponses) return;
		setErrors({ payload: false, type: ERRORS.AssessmentActivityResponses });
	};

	const selectTreatmentPlanHAndler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ payload: value, type: ACTIONS.setTreatmentPlans });
		if (!errors.TreatmentPlanResponses) return;
		setErrors({ payload: false, type: ERRORS.TreatmentPlanResponses });
	};

	const selectPeopleAttendanceHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ payload: value, type: ACTIONS.setParticipants });
		if (!errors.ParticipantResponses) return;
		setErrors({ payload: false, type: ERRORS.ParticipantResponses });
	};

	const selectLocationHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setLocationId });
		if (!errors.LocationId) return;
		setErrors({ payload: false, type: ERRORS.LocationId });
	};

	const sessionSummaryHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setSessionSummary });
		if (!errors.SessionSummary) return;
		setErrors({ payload: false, type: ERRORS.SessionSummary });
	};

	const dataCollectedHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setDataCollected });
		if (!errors.DataCollected) return;
		setErrors({ payload: false, type: ERRORS.DataCollected });
	};

	const goalsCreatedHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setGoalsCreated });
		if (!errors.GoalsCreated) return;
		setErrors({ payload: false, type: ERRORS.GoalsCreated });
	};

	const signatureHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value.toUpperCase(), type: ACTIONS.setSignature });
		if (!errors.Signature) return;
		setErrors({ payload: false, type: ERRORS.Signature });
	};

	const validateSignatureHandler = () => {
		if (validateName(data.signature)) {
			save();

			return;
		}
		setErrors({ payload: true, type: ERRORS.Signature });
	};

	const assessmentParticipantOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setAssessmentParticipantOther });
		if (!errors.AssessmentParticipantOther) return;
		setErrors({ payload: false, type: ERRORS.AssessmentParticipantOther });
	};

	const assessmentActivityOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setAssessmentActivityOther });
		if (!errors.AssessmentActivityOther) return;
		setErrors({ payload: false, type: ERRORS.AssessmentActivityOther });
	};

	const isOtherSelectedForParticipiants = data.participantResponses.find(
		(x) => peopleAttendanceList.find((y) => y.id === x)?.text === 'Other',
	);

	const isOtherSelectedForAssessmentActivity = data.assessmentActivityResponses.find(
		(x) => assessmentActivityList.find((y) => y.id === x)?.text === 'Other',
	);

	return (
		<div>
			<TimeInfo disabled={getFieldDisabled('startTime')} save={save} />
			<CustomSelect
				label="Location:"
				data={locationList}
				value={data.locationId ? data.locationId : ''}
				setValue={selectLocationHandler}
				className="marginBottom16"
				disabled={getFieldDisabled('locationId')}
				error={errors.LocationId}
				onBlur={save}
			/>
			{isTelehealth && (
				<TelehealthLocations
					setData={setData}
					data={data}
					disabled={getFieldDisabled('clientTelehealthLocationId')}
					save={save}
				/>
			)}
			<MultipleSelect
				label="Session Participants:"
				data={peopleAttendanceList}
				initialValue={data.participantResponses}
				setValue={selectPeopleAttendanceHandler}
				className="marginBottom16"
				error={errors.ParticipantResponses}
				disabled={getFieldDisabled('participantResponses')}
				onBlur={save}
			/>
			{isOtherSelectedForParticipiants && (
				<CustomInput
					label="If other, please describe: "
					value={data.assessmentParticipantOther}
					onChange={assessmentParticipantOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('assessmentParticipantOther')}
					onBlur={save}
					error={errors.AssessmentParticipantOther}
				/>
			)}
			<MultipleSelect
				label="Assessment tools used: "
				data={assessmentToolsList}
				initialValue={data.assessmentToolResponses}
				setValue={selectAssessmentToolHandler}
				className="marginBottom16"
				error={errors.AssessmentToolResponses}
				disabled={getFieldDisabled('assessmentToolResponses')}
				onBlur={save}
			/>
			<MultipleSelect
				label="Assessment activities: "
				data={assessmentActivityList}
				initialValue={data.assessmentActivityResponses}
				setValue={selectAssessmentActivityHandler}
				className="marginBottom16"
				error={errors.AssessmentActivityResponses}
				disabled={getFieldDisabled('assessmentActivityResponses')}
				onBlur={save}
			/>
			{isOtherSelectedForAssessmentActivity && (
				<CustomInput
					label="If other, please describe: "
					value={data.assessmentActivityOther}
					onChange={assessmentActivityOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('assessmentActivityOther')}
					onBlur={save}
					error={errors.AssessmentActivityOther}
				/>
			)}
			<MultipleSelect
				label="Treatment plan:"
				data={treatmentPlanList}
				initialValue={data.treatmentPlanResponses}
				setValue={selectTreatmentPlanHAndler}
				className="marginBottom16"
				error={errors.TreatmentPlanResponses}
				disabled={getFieldDisabled('treatmentPlanResponses')}
				onBlur={save}
			/>
			<CustomInput
				label="Data collected/Behaviors observed:"
				value={data.dataCollected}
				onChange={dataCollectedHandler}
				multiline
				className="marginBottom16"
				error={errors.DataCollected}
				disabled={getFieldDisabled('dataCollected')}
				onBlur={save}
			/>
			<CustomInput
				label="Goals created:"
				value={data.goalsCreated}
				onChange={goalsCreatedHandler}
				multiline
				className="marginBottom16"
				error={errors.GoalsCreated}
				disabled={getFieldDisabled('goalsCreated')}
				onBlur={save}
			/>
			<Text title="Session Details" type="h4" font="header" fontWeight="semiBold" className="marginBottom16 title" />
			<Targets />
			<Behaviors />
			<CustomInput
				label="Session Summary: "
				value={data.sessionSummary}
				onChange={sessionSummaryHandler}
				className="marginBottom8"
				multiline
				error={errors.SessionSummary}
				disabled={getFieldDisabled('sessionSummary')}
				onBlur={save}
			/>
			<SignatureInput
				value={data.signature}
				setValue={signatureHandler}
				error={errors.Signature}
				disabled={getFieldDisabled('signature')}
				onBlur={validateSignatureHandler}
			/>
			<div>
				<Text title="Date of signature:" type="psm" fontWeight="semiBold" />
				<Text title={todayString} type="psm" />
			</div>
		</div>
	);
};

export default AssessmentData;
